import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Image, Input, Row, Select, Space, Switch, Tag, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTheme } from "@/contexts/ThemeContext";
import { createBanner, deleteBanner, getBanners, toggleBannerStatus, uploadBannerImage } from "@/services/api";

const placementOptions = [
  { label: "Site Top", value: "site-top" },
  { label: "Home Top", value: "home-top" },
  { label: "Home Inline", value: "home-inline" },
  { label: "Home Sidebar", value: "home-sidebar" },
  { label: "Article Top", value: "article-top" },
  { label: "Article Inline", value: "article-inline" },
  { label: "Article Sidebar", value: "article-sidebar" },
  { label: "Category Top", value: "category-top" },
  { label: "Category Inline", value: "category-inline" },
  { label: "Category Sidebar", value: "category-sidebar" },
  { label: "Sidebar", value: "sidebar" },
  { label: "Footer", value: "footer" },
];

const placementLabel = (value) =>
  placementOptions.find((item) => item.value === value)?.label || value || "Site Top";

const extractUploadedImage = (payload) =>
  payload?.path || payload?.data?.path || payload?.url || payload?.data?.url || "";

const Banners = () => {
  const { isDark } = useTheme();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banners, setBanners] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const cardStyle = useMemo(
    () => ({
      borderRadius: 0,
      border: `1px solid ${isDark ? "#27272A" : "#E4E4E7"}`,
      background: isDark ? "#18181B" : "#FFFFFF",
    }),
    [isDark],
  );

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await getBanners();
      if (response?.isError) {
        throw response.error;
      }

      setBanners(response?.data?.data || []);
    } catch (error) {
      message.error("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const resetForm = () => {
    form.resetFields();
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleCreate = async (values) => {
    if (!selectedFile) {
      message.warning("Please select a banner image");
      return;
    }

    setSaving(true);
    try {
      const uploadResult = await uploadBannerImage(selectedFile);
      if (uploadResult?.isError) {
        throw uploadResult.error;
      }

      const image = extractUploadedImage(uploadResult?.data);
      if (!image) {
        throw new Error("Image upload did not return a path.");
      }

      const payload = {
        title: values.title?.trim(),
        link: values.link?.trim(),
        placement: values.placement || "site-top",
        sortOrder: values.sortOrder ?? 0,
        active: values.active ?? true,
        image,
      };

      const response = await createBanner(payload);
      if (response?.status !== 200 && response?.status !== 201) {
        throw new Error("Failed to create banner");
      }

      message.success("Banner created successfully");
      resetForm();
      fetchBanners();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to create banner");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (uid) => {
    try {
      const response = await toggleBannerStatus(uid);
      if (response?.status !== 200) {
        throw new Error("Failed to update banner status");
      }
      message.success("Banner status updated");
      fetchBanners();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to update banner status");
    }
  };

  const handleDelete = async (uid) => {
    try {
      const response = await deleteBanner(uid);
      if (response?.status !== 200) {
        throw new Error("Failed to delete banner");
      }
      message.success("Banner deleted successfully");
      fetchBanners();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to delete banner");
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontSize: 28,
            fontWeight: 700,
            fontFamily: "IBM Plex Sans, sans-serif",
            letterSpacing: "-0.02em",
            marginBottom: 8,
          }}
        >
          Banners
        </h1>
        <p style={{ fontSize: 14, color: isDark ? "#A1A1AA" : "#71717A", margin: 0 }}>
          Upload promotional banners and control where they appear in the news app.
        </p>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} xl={10}>
          <Card style={cardStyle} title="Add Banner" extra={<Button icon={<ReloadOutlined />} onClick={fetchBanners}>Refresh</Button>}>
            <Form layout="vertical" form={form} onFinish={handleCreate} initialValues={{ placement: "site-top", active: true, sortOrder: 0 }}>
              <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter a banner title" }]}>
                <Input placeholder="e.g. Summer Offer" style={{ borderRadius: 0 }} />
              </Form.Item>

              <Form.Item name="link" label="Target Link">
                <Input placeholder="https://example.com" style={{ borderRadius: 0 }} />
              </Form.Item>

              <Form.Item name="placement" label="Placement" rules={[{ required: true, message: "Please choose a placement" }]}>
                <Select options={placementOptions} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item name="sortOrder" label="Sort Order">
                <Input type="number" min={0} placeholder="0" style={{ borderRadius: 0 }} />
              </Form.Item>

              <Form.Item name="active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item label="Banner Image">
                <input type="file" accept="image/*" onChange={handleFileChange} />
              </Form.Item>

              {previewUrl ? (
                <div style={{ marginBottom: 16 }}>
                  <Image src={previewUrl} alt="Banner preview" style={{ maxHeight: 180, objectFit: "cover" }} preview={false} />
                </div>
              ) : null}

              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={saving}
                  style={{ borderRadius: 0 }}
                >
                  Create Banner
                </Button>
                <Button style={{ borderRadius: 0 }} onClick={resetForm}>
                  Clear
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} xl={14}>
          <Card style={cardStyle} title="Uploaded Banners">
            <div style={{ display: "grid", gap: 16 }}>
              {banners.length ? (
                banners.map((banner) => (
                  <Card
                    key={banner.uid}
                    size="small"
                    style={{
                      borderRadius: 0,
                      border: `1px solid ${isDark ? "#27272A" : "#E4E4E7"}`,
                      background: isDark ? "#09090B" : "#FAFAFA",
                    }}
                    bodyStyle={{ padding: 16 }}
                  >
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                        <Image
                          src={banner.image}
                          alt={banner.title}
                          width={160}
                          height={90}
                          style={{ objectFit: "cover", borderRadius: 0 }}
                          preview={false}
                        />
                        <div>
                          <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 6 }}>
                            {banner.title}
                          </Typography.Title>
                          <div style={{ marginBottom: 8 }}>
                            <Tag color={banner.active ? "green" : "default"}>{banner.active ? "Enabled" : "Disabled"}</Tag>
                            <Tag>{placementLabel(banner.placement)}</Tag>
                          </div>
                          {banner.link ? (
                            <Typography.Text type="secondary" style={{ display: "block", wordBreak: "break-all" }}>
                              {banner.link}
                            </Typography.Text>
                          ) : null}
                        </div>
                      </div>

                      <Space>
                        <Switch
                          checked={Boolean(banner.active)}
                          onChange={() => handleToggle(banner.uid)}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                        />
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(banner.uid)}
                          style={{ borderRadius: 0 }}
                        >
                          Delete
                        </Button>
                      </Space>
                    </div>
                  </Card>
                ))
              ) : (
                <div style={{ padding: 24, color: isDark ? "#A1A1AA" : "#71717A" }}>
                  {loading ? "Loading banners..." : "No banners uploaded yet."}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Banners;
